/**
 * Changes made:
 * - 2025-11-06: Updated h2 tag to display "Jak działa Autaro.pl?" with single color for SEO consistency
 */

import { Users2, Banknote } from "lucide-react";
import spImg001 from "@/assets/sp-img-001.png";
import spImg002 from "@/assets/sp-img-002.png";
import spImg003 from "@/assets/sp-img-003.png";
import spImg004 from "@/assets/sp-img-004.png";

interface HowItWorksProps {
  showStepNumbers?: boolean;
}

export const HowItWorks = ({ showStepNumbers = true }: HowItWorksProps) => {
  return (
    <section className="py-12 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-5xl font-heebo font-bold text-center mb-20" style={{ fontWeight: 700 }}>
          <span className="text-heading">Jak działa</span> <span className="text-primary">Autaro.pl?</span>
        </h2>
        <div className="grid md:grid-cols-4 gap-8">
          {[
            {
              icon: null,
              image: spImg001,
              text: "Otrzymaj wycenę samochodu",
              step: "01",
              description: "Wypełnij wymagane dane, aby otrzymać wycenę swojego samochodu"
            },
            {
              icon: null,
              image: spImg002,
              text: "Uzupełnij profil samochodu",
              step: "02",
              description: "Dodaj zdjęcia samochodu, ewentualnych uszkodzeń oraz informacje o historii serwisowej w kilku prostych krokach"
            },
            {
              icon: null,
              image: spImg003,
              text: "Otrzymaj najwyższą ofertę",
              step: "03",
              description: "Przedstawiamy Twój samochód naszej sieci zweryfikowanych komisów i w ciągu 24–48 godzin przekazujemy Ci najwyższą ofertę. Bez opłat, bez stresu."
            },
            {
              icon: null,
              image: spImg004,
              text: "Sprzedaj samochód i otrzymaj pieniądze",
              step: "04",
              description: "Zwycięski komis zorganizuje odbiór twojego samochodu tam, gdzie Ci wygodnie, i zapłaci na miejscu."
            }
          ].map((item, index) => (
            <div key={index} className="group text-center h-full">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden h-full flex flex-col">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-16 -mt-16 transition-all group-hover:scale-110" />
                
                {/* Image Section - Top of the box */}
                <div className="relative p-4 pb-2">
                  <div className="w-full aspect-square mx-auto mb-4 relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white rounded-2xl flex items-start justify-center pt-2">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.text}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform" 
                        />
                      ) : (
                        <item.icon className="w-24 h-24 text-primary group-hover:scale-110 transition-transform" />
                      )}
                    </div>
                    {showStepNumbers && (
                      <div className="absolute -top-2 -right-2 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-base shadow-lg z-10">
                        {item.step}
                      </div>
                    )}
                  </div>
                </div>

                {/* Text Section - Bottom of the box */}
                <div className="px-6 pb-8 flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-3 text-heading group-hover:text-primary transition-colors">
                      {item.text}
                    </h3>
                    <p className="text-subtitle text-sm group-hover:text-primary/80 transition-colors leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};